<template>
  <main class="trade-page">
    <AppNavBar :title="t('card.tradeDetail')" />

    <section v-if="trade" class="trade-card">
      <div class="trade-amount" :class="{ positive: Number(trade.amount || 0) > 0 }">
        {{ formatTradeAmount(trade) }}
      </div>
      <div class="trade-status">
        <div :class="statusClass">{{ statusText }}</div>
        <p v-if="Number(trade.status) === 3">{{ t('card.tradeProcessingTip') }}</p>
        <p v-if="failReasonText">{{ `(${t('card.failedReason')}: ${failReasonText})` }}</p>
      </div>

      <div class="detail-list">
        <div class="detail-row">
          <span>{{ t('card.cardNumber') }}</span>
          <div data-strong>{{ displayValue(trade.cardNum) }}</div>
        </div>
        <div class="detail-row">
          <span>{{ t('card.tradeType') }}</span>
          <div data-strong>{{ displayValue(trade.typeMsg) }}</div>
        </div>
        <div v-if="detailText" class="detail-row">
          <span>{{ t('card.merchant') }}</span>
          <div data-strong>{{ detailText }}</div>
        </div>
        <div class="detail-row">
          <span>{{ t('card.amount') }}</span>
          <div data-strong>{{ amountText }}</div>
        </div>
        <div class="detail-row">
          <span>{{ t('wallet.fee') }}</span>
          <div data-strong>{{ feeText }}</div>
        </div>
        <div class="detail-row">
          <span>{{ t('card.orderNumber') }}</span>
          <button type="button" class="copy-btn" @click="copyText(orderIdText)">
            <div data-strong>{{ orderIdText }}</div>
            <app-image src="@/assets/images/common_copy_icon_gray.png" alt="" />
          </button>
        </div>
        <div class="detail-row">
          <span>{{ t('card.tradeTime') }}</span>
          <div data-strong>{{ formatCardTimestamp(trade.createTime) || '--' }}</div>
        </div>
      </div>
    </section>

    <section v-else class="empty-card">
      <van-empty :description="t('card.noTradeDetail')" image="search" />
    </section>
  </main>
</template>

<script setup>
import { computed } from 'vue';
import { showFailToast, showToast } from 'vant';
import { showPassedToast } from '@/utils/toast';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useCardBillStore } from '@/stores/card-bill';
import { copyToClipboard } from '@/utils/clipboard';
import { formatCardTimestamp, formatTradeAmount } from '@/view/card/utils';

const { t } = useI18n();
const router = useRouter();
const cardBillStore = useCardBillStore();

const trade = computed(() => cardBillStore.currentTrade);
const statusText = computed(() => {
  if (!trade.value) {
    return '';
  }
  if (Number(trade.value.status) === 1) return t('card.success');
  if (Number(trade.value.status) === 2) return t('card.failed');
  if (Number(trade.value.status) === 3) return t('card.processing');
  return '';
});
const statusClass = computed(() => {
  if (Number(trade.value?.status) === 1) return 'success';
  if (Number(trade.value?.status) === 2) return 'failed';
  if (Number(trade.value?.status) === 3) return 'processing';
  return '';
});
const failReasonText = computed(() => {
  const reason = String(trade.value?.failReason || '').trim();
  return reason && reason.toLowerCase() !== 'null' ? reason : '';
});
const detailText = computed(() => {
  const detail = String(trade.value?.detail || '').trim();
  return detail && detail.toLowerCase() !== 'null' ? detail : '';
});
const amountText = computed(() => formatFieldWithSymbol(trade.value?.amount, trade.value?.symbol));
const feeText = computed(() => formatFieldWithSymbol(trade.value?.fee, trade.value?.symbol));
const orderIdText = computed(() => displayValue(trade.value?.orderId));

function displayValue(value) {
  if (value === undefined || value === null) return '';
  return String(value);
}

function formatFieldWithSymbol(value, symbol) {
  return `${displayValue(value)} ${displayValue(symbol)}`.trim();
}

async function copyText(text) {
  if (!text) return;
  const copied = await copyToClipboard(text);
  if (copied) {
    showPassedToast(t('wallet.copySuccess'));
    return;
  }
  showFailToast(t('wallet.copyFailed'));
}
</script>

<style scoped lang="scss">
.trade-page {
  min-height: var(--app-viewport-height);
  padding: 14px;
  background: #f5f5f5;
}

.trade-card,
.empty-card {
  border-radius: 8px;
  background: #ffffff;
}

.trade-card {
  padding: 24px 16px 8px;
}

.trade-amount {
  color: #f53f3f;
  font-size: 28px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 5px;
  &.positive {
    color: #07B077;
  }
}

.trade-status {
  text-align: center;

  p {
    margin: 8px 0 0;
    color: #86909c;
    font-size: 12px;
    line-height: 18px;
  }

  .success {
    color: #86909c;
    font-size: 14px;
  }

  .failed {
    color: #f53f3f;
    font-size: 14px;
  }

  .processing {
    color: var(--wp-color-theme);
    font-size: 14px;
  }
}

.detail-list {
  margin-top: 20px;
}

.detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 54px;
  border-top: 1px dashed #e5e6eb;
  gap: 12px;

  &:first-child {
    border-top: 0;
  }

  span {
    color: #666666;
    font-size: 14px;
  }

  [data-strong] {
    color: #333333;
    font-size: 14px;
    font-weight: 500;
    text-align: right;
    word-break: break-all;
  }
}

.copy-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  .app-image {
    width: 18px;
    height: 18px;
  }
}

.empty-card {
  padding: 28px 16px;
}
</style>
